import { useSearchParams } from 'react-router-dom';
import { DatePicker, SegmentedControl } from '@bcecloud/portal-ui';
import { STATS_TIME_ZONE } from '@/lib/report-window';
import {
  SALES_EARLIEST_DAY,
  SALES_RANGE,
  SALES_RANGE_OPTIONS,
  SALES_TIMEFRAME_PARAM,
  DEFAULT_SALES_RANGE,
  parseSalesTimeframe,
  type SalesRange,
  type SalesTimeframe,
  type SalesWindow,
} from '@/lib/sales';

/** How far back a fresh custom window reaches when no window is showing yet. */
const CUSTOM_FALLBACK_DAYS = 90;
const DAY_MS = 86_400_000;

/** Today as `YYYY-MM-DD` on Eastern time, the zone the API resolves windows in. */
function easternToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: STATS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

function daysBefore(day: string, days: number): string {
  return new Date(new Date(`${day}T00:00:00.000Z`).getTime() - days * DAY_MS).toISOString().slice(0, 10);
}

export interface SalesTimeframeState {
  readonly timeframe: SalesTimeframe;
  /** Picks a preset; custom starts from `seed` (the window on screen) so it is valid at once. */
  readonly setRange: (range: SalesRange, seed?: Pick<SalesWindow, 'from' | 'to'>) => void;
  /** Moves one end of a custom window, pushing the other along so from never passes to. */
  readonly setCustomDay: (end: 'from' | 'to', day: string) => void;
}

/**
 * The sales scorecard's timeframe, kept in the URL (`?range=`, plus `from`
 * and `to` for custom) so a view can be shared. Other params (search, sort,
 * the profit window) are left alone; a page param resets.
 *
 * @returns The timeframe and its setters.
 */
export function useSalesTimeframe(): SalesTimeframeState {
  const [params, setParams] = useSearchParams();
  const timeframe = parseSalesTimeframe(params);

  function write(next: { range: SalesRange; from?: string; to?: string }): void {
    setParams(
      (previous) => {
        const nextParams = new URLSearchParams(previous);
        nextParams.delete(SALES_TIMEFRAME_PARAM.from);
        nextParams.delete(SALES_TIMEFRAME_PARAM.to);
        nextParams.delete('page');
        if (next.range === DEFAULT_SALES_RANGE) nextParams.delete(SALES_TIMEFRAME_PARAM.range);
        else nextParams.set(SALES_TIMEFRAME_PARAM.range, next.range);
        if (next.from !== undefined && next.to !== undefined) {
          nextParams.set(SALES_TIMEFRAME_PARAM.from, next.from);
          nextParams.set(SALES_TIMEFRAME_PARAM.to, next.to);
        }
        return nextParams;
      },
      { replace: true },
    );
  }

  function setRange(range: SalesRange, seed?: Pick<SalesWindow, 'from' | 'to'>): void {
    if (range !== SALES_RANGE.custom) {
      write({ range });
      return;
    }
    if (timeframe.range === SALES_RANGE.custom) return;
    const today = easternToday();
    write({
      range,
      from: seed?.from ?? daysBefore(today, CUSTOM_FALLBACK_DAYS - 1),
      to: seed?.to ?? today,
    });
  }

  function setCustomDay(end: 'from' | 'to', day: string): void {
    if (timeframe.range !== SALES_RANGE.custom || day === '') return;
    const from = end === 'from' ? day : timeframe.from > day ? day : timeframe.from;
    const to = end === 'to' ? day : timeframe.to < day ? day : timeframe.to;
    write({ range: SALES_RANGE.custom, from, to });
  }

  return { timeframe, setRange, setCustomDay };
}

export interface SalesTimeframePickerProps {
  readonly state: SalesTimeframeState;
  /** The window on screen, to seed a custom range; absent while loading. */
  readonly window?: SalesWindow | undefined;
}

const dayFormat = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

/** "Jul 7, 2026 to Oct 4, 2026", or one day. */
export function windowDatesLabel(window: Pick<SalesWindow, 'from' | 'to'>): string {
  const from = dayFormat.format(new Date(`${window.from}T00:00:00.000Z`));
  const to = dayFormat.format(new Date(`${window.to}T00:00:00.000Z`));
  return from === to ? from : `${from} to ${to}`;
}

/**
 * The timeframe control on the sales dashboard and the rep page: one click
 * per preset, and two design-system date pickers for a custom window.
 *
 * @param props - The URL-backed state and the window on screen.
 * @returns The control, with the resolved dates under it.
 */
export function SalesTimeframePicker({ state, window }: SalesTimeframePickerProps): React.ReactElement {
  const { timeframe, setRange, setCustomDay } = state;
  const today = easternToday();
  return (
    <div className="flex flex-wrap items-end gap-3">
      <SegmentedControl<SalesRange>
        label="Timeframe"
        value={timeframe.range}
        onChange={(range) => setRange(range, window)}
        options={SALES_RANGE_OPTIONS}
      />
      {timeframe.range === SALES_RANGE.custom ? (
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label htmlFor="sales-from" className="mb-1 block text-portal-micro font-bold text-portal-muted">
              From
            </label>
            <DatePicker
              id="sales-from"
              value={timeframe.from}
              min={SALES_EARLIEST_DAY}
              max={today}
              placeholder="Start"
              onChange={(day) => setCustomDay('from', day)}
            />
          </div>
          <div>
            <label htmlFor="sales-to" className="mb-1 block text-portal-micro font-bold text-portal-muted">
              To
            </label>
            <DatePicker
              id="sales-to"
              value={timeframe.to}
              min={SALES_EARLIEST_DAY}
              max={today}
              placeholder="End"
              onChange={(day) => setCustomDay('to', day)}
            />
          </div>
        </div>
      ) : null}
      {window ? (
        <p className="pb-2 text-portal-micro text-portal-muted" aria-live="polite">
          {windowDatesLabel(window)}
        </p>
      ) : null}
    </div>
  );
}
